import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { businessApi } from '../lib/api';
import {
  IconDashboard, IconBusinesses, IconWebsiteAudit, IconKeywords,
  IconSEOIntelligence, IconCompetitors, IconIntegrations, IconReports,
  IconAIAssistant, IconSettings, IconReviews, IconGemini
} from './icons';

interface SidebarProps {
  mobileOpen?: boolean;
  onClose?: () => void;
}

type NavItem = {
  label: string;
  to: string;
  icon: React.ReactNode;
  active: boolean;
  disabled?: boolean;
  badge?: string;
};

function NavIcon({ icon }: { icon: React.ReactNode }) {
  return <span className="sidebar-nav-glyph" aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>;
}

export function Sidebar({ mobileOpen = false, onClose }: SidebarProps) {
  const { user, organization } = useAuth();
  const location = useLocation();
  const params = useParams<{ businessId?: string }>();

  const [activeBizId, setActiveBizId] = useState<number | null>(() => {
    if (params.businessId) return parseInt(params.businessId, 10);
    const saved = localStorage.getItem('active_dashboard_business_id');
    return saved ? parseInt(saved, 10) : null;
  });

  useEffect(() => {
    if (params.businessId) {
      const id = parseInt(params.businessId, 10);
      if (!isNaN(id)) {
        setActiveBizId(id);
        localStorage.setItem('active_dashboard_business_id', String(id));
      }
    } else {
      const saved = localStorage.getItem('active_dashboard_business_id');
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) setActiveBizId(parsed);
      }
    }
  }, [params.businessId, location.pathname]);

  useEffect(() => {
    function handleBizChange(e: Event) {
      const customEvent = e as CustomEvent<number>;
      if (customEvent.detail) {
        setActiveBizId(customEvent.detail);
        return;
      }
      const saved = localStorage.getItem('active_dashboard_business_id');
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) setActiveBizId(parsed);
      }
    }

    window.addEventListener('active-business-change', handleBizChange);
    window.addEventListener('storage', handleBizChange);
    return () => {
      window.removeEventListener('active-business-change', handleBizChange);
      window.removeEventListener('storage', handleBizChange);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    businessApi
      .list()
      .then((list) => {
        if (!mounted || list.length === 0) return;
        setActiveBizId((prev) => {
          if (prev && list.some((b) => b.id === prev)) return prev;
          const saved = localStorage.getItem('active_dashboard_business_id');
          const savedNum = saved ? parseInt(saved, 10) : null;
          const matched = savedNum ? list.find((b) => b.id === savedNum) : null;
          const chosen = matched ? matched.id : list[0].id;
          localStorage.setItem('active_dashboard_business_id', String(chosen));
          return chosen;
        });
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [organization?.id]);

  const initials = user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : '??';
  const path = location.pathname;
  const bizPath = (suffix: string) => (activeBizId ? `/businesses/${activeBizId}/${suffix}` : '/businesses');

  const seoAccess = user?.isSuperAdmin || user?.moduleAccess?.['seo'] !== false;
  const aiAccess = user?.isSuperAdmin || user?.moduleAccess?.['ai'] !== false;
  const reportsAccess = user?.isSuperAdmin || user?.moduleAccess?.['reports'] !== false;

  // Determine exactly ONE active navigation key for the current path
  const activeKey = (() => {
    if (path === '/dashboard' || path.endsWith('/dashboard')) return 'dashboard';
    if (path.includes('/seo')) return 'seo';
    if (path.includes('/keywords')) return 'keywords';
    if (path.includes('/intelligence')) return 'intelligence';
    if (path.includes('/competitors')) return 'competitors';
    if (path.includes('/integrations')) return 'integrations';
    if (path.includes('/reports')) return 'reports';
    if (path.includes('/ai-assistant')) return 'ai-assistant';
    if (path.includes('/ai-config')) return 'ai-config';
    if (path.startsWith('/users')) return 'users';
    if (path.startsWith('/settings')) return 'settings';
    if (path.includes('/reviews')) return 'reviews';
    if (path === '/businesses' || path.startsWith('/businesses/new')) return 'businesses';
    return '';
  })();

  const groups: Array<{ label: string; items: NavItem[] }> = [
    {
      label: 'Overview',
      items: [
        { label: 'Dashboard', to: '/dashboard', icon: <IconDashboard width={18} height={18} />, active: activeKey === 'dashboard' },
        {
          label: 'Businesses',
          to: '/businesses',
          icon: <IconBusinesses width={18} height={18} />,
          active: activeKey === 'businesses',
        },
      ],
    },
    ...(seoAccess ? [{
      label: 'SEO',
      items: [
        { label: 'Website Audit', to: bizPath('seo'), icon: <IconWebsiteAudit width={18} height={18} />, active: activeKey === 'seo' },
        { label: 'Keywords', to: bizPath('keywords'), icon: <IconKeywords width={18} height={18} />, active: activeKey === 'keywords' },
        { label: 'SEO Intelligence', to: bizPath('intelligence'), icon: <IconSEOIntelligence width={18} height={18} />, active: activeKey === 'intelligence' },
        { label: 'Competitors', to: bizPath('competitors'), icon: <IconCompetitors width={18} height={18} />, active: activeKey === 'competitors' },
      ],
    }] : []),
    {
      label: 'Google',
      items: [
        { label: 'Integrations', to: bizPath('integrations'), icon: <IconIntegrations width={18} height={18} />, active: activeKey === 'integrations' },
      ],
    },
    ...(reportsAccess ? [{
      label: 'Reporting',
      items: [
        { label: 'Reports', to: bizPath('reports'), icon: <IconReports width={18} height={18} />, active: activeKey === 'reports' },
      ],
    }] : []),
    ...(aiAccess ? [{
      label: 'AI',
      items: [
        { label: 'AI Assistant', to: bizPath('ai-assistant'), icon: <IconAIAssistant width={18} height={18} />, active: activeKey === 'ai-assistant' },
      ],
    }] : []),
    {
      label: 'Account',
      items: [
        { label: 'Settings', to: '/settings', icon: <IconSettings width={18} height={18} />, active: activeKey === 'settings' },
        ...(user?.isSuperAdmin ? [
          { label: 'AI Configuration', to: '/admin/ai-config', icon: <IconGemini width={18} height={18} />, active: activeKey === 'ai-config' },
          { label: 'User Management', to: '/users', icon: <IconSettings width={18} height={18} />, active: activeKey === 'users' },
        ] : []),
        { label: 'Reviews', to: bizPath('reviews'), icon: <IconReviews width={18} height={18} />, active: activeKey === 'reviews', disabled: false, badge: 'Soon' },
      ],
    },
  ];

  return (
    <aside className={`sidebar ${mobileOpen ? 'is-open' : ''}`} aria-label="Primary navigation">
      <div className="sidebar-header">
        <NavLink to="/dashboard" className="sidebar-logo" onClick={onClose}>
          <span className="sidebar-logo-mark" aria-hidden="true">L</span>
          <span className="sidebar-logo-copy">
            <span className="sidebar-logo-title">Local SEO AI</span>
            <span className="sidebar-logo-subtitle">{organization?.name ?? 'Organization'}</span>
          </span>
        </NavLink>
      </div>

      <nav className="sidebar-nav">
        {groups.map((group) => (
          <div className="sidebar-section" key={group.label}>
            <div className="sidebar-section-label">{group.label}</div>
            {group.items.map((item) => (
              <Link
                key={`${group.label}-${item.label}`}
                to={item.to}
                onClick={onClose}
                className={`sidebar-nav-item ${item.active ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
                aria-disabled={item.disabled ? 'true' : undefined}
                title={item.badge === 'Soon' ? 'Reviews is planned for a later release.' : undefined}
              >
                <NavIcon icon={item.icon} />
                <span className="sidebar-nav-label">{item.label}</span>
                {item.badge && <span className="sidebar-nav-badge">{item.badge}</span>}
              </Link>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{initials}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.firstName} {user?.lastName}</div>
            <div className="sidebar-user-org">{organization?.name}</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
